import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Clock,
  Package,
  Calendar,
  HelpCircle,
  Users,
  Bot,
  MessageCircle,
  FileSpreadsheet,
} from 'lucide-react'
import { botSettingsApi } from '@/services/botSettingsApi'
import { businessHoursApi } from '@/services/businessHoursApi'
import { getFAQs } from '@/lib/api/faqs'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'
import { useVerticalUI } from '@/lib/verticalUI/useVerticalUI'
import type { VerticalUiProfile } from '@/lib/verticalUI'
import { useChannelInstances } from './useChannelInstances'

export interface SetupTask {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  link: string
  completed: boolean
  priority: 'high' | 'medium' | 'low'
  estimatedTime: string
}

export interface SetupStatusSources {
  hasConnectedChannel: boolean
  hasProducts: boolean
  hasCustomizedBot: boolean
  hasEscalationConfigured: boolean
  hasFaqs: boolean
  businessHoursEnabled: boolean
}

/**
 * Pasos de puesta en marcha con su estado real. El estado de cada tarea sale de
 * la configuración del negocio; el catálogo se resuelve con el conteo que ya
 * entrega el dashboard (`activeProducts`), nunca descargando todos los productos.
 */
export function buildSetupTasks(profile: VerticalUiProfile, status: SetupStatusSources): SetupTask[] {
  const isFulfillment = profile.operational?.hasFulfillment ?? true
  const items = profile.terminology.itemsPluralName.toLowerCase()
  const staff = profile.terminology.staffRoleLabel.toLowerCase()
  const customer = profile.terminology.customerRoleLabel.toLowerCase()

  return [
    {
      id: 'channel',
      title: 'Conectar tu WhatsApp',
      description: 'Vincula tu número o escanea el QR para que tu asistente pueda chatear',
      icon: MessageCircle,
      link: '/app/instances',
      completed: status.hasConnectedChannel,
      priority: 'high',
      estimatedTime: '2 min',
    },
    {
      id: 'products',
      title: `Cargar ${items} en tu catálogo`,
      description: isFulfillment
        ? 'Permite al asistente recomendar productos y reconocer fotos enviadas por tus clientes'
        : `Permite al asistente agendar y cotizar tus ${items} con tus ${customer}s`,
      icon: isFulfillment ? Package : Calendar,
      link: '/app/catalog',
      completed: status.hasProducts,
      priority: 'high',
      estimatedTime: '3 min',
    },
    {
      id: 'bot-config',
      title: 'Estilo de atención del Asistente',
      description: 'Personaliza su nombre, mensaje de bienvenida y activa el modo consultivo',
      icon: Bot,
      link: '/app/bot-settings',
      completed: status.hasCustomizedBot,
      priority: 'medium',
      estimatedTime: '2 min',
    },
    {
      id: 'escalation',
      title: 'Atención humana (Escalamiento)',
      description: `Define qué ${staff} o equipo atiende cuando un ${customer} pide hablar con una persona`,
      icon: Users,
      link: '/app/bot-settings/escalation',
      completed: status.hasEscalationConfigured,
      priority: 'medium',
      estimatedTime: '2 min',
    },
    {
      id: 'faqs',
      title: 'Preguntas Frecuentes (FAQs)',
      description: isFulfillment
        ? 'Respuestas automáticas sobre envíos, pagos y políticas de tu negocio'
        : 'Respuestas automáticas sobre atención presencial, requisitos y métodos de pago',
      icon: HelpCircle,
      link: '/app/bot-settings/faq',
      completed: status.hasFaqs,
      priority: 'medium',
      estimatedTime: '3 min',
    },
    {
      id: 'business-hours',
      title: 'Horarios de atención (Opcional)',
      description: 'Por defecto tu bot responde 24/7. Actívalo solo si deseas limitar la atención a ciertas horas',
      icon: Clock,
      link: '/app/bot-settings/advanced',
      completed: status.businessHoursEnabled,
      priority: 'low',
      estimatedTime: '2 min',
    },
    ...(isFulfillment
      ? [
          {
            id: 'inventory-sync',
            title: 'Sincronizar Google Sheets o ERP (Opcional)',
            description: 'Conecta tu hoja de cálculo para que el asistente consulte precios y descuente existencias en cada venta',
            icon: FileSpreadsheet,
            link: '/app/inventory-sync',
            completed: false,
            priority: 'low' as const,
            estimatedTime: '2 min',
          },
        ]
      : []),
  ]
}

interface UseSetupStatusOptions {
  /** Conteo de productos activos que ya calcula `/dashboard/stats`. */
  activeProducts?: number
}

/**
 * Estado de puesta en marcha en React Query: una consulta por fuente, todas con
 * clave tenant-scoped. El checklist ya no hace fetch propio ni descarga el catálogo.
 */
export function useSetupStatus({ activeProducts }: UseSetupStatusOptions = {}) {
  const tenantId = useAuthStore((state) => state.tenantId)
  const { profile } = useVerticalUI()
  const enabled = Boolean(tenantId)

  const botSettings = useQuery({
    queryKey: queryKeys.botSettings(tenantId),
    queryFn: () => botSettingsApi.getSettings(),
    enabled,
    staleTime: 60_000,
    refetchIntervalInBackground: false,
  })

  const channels = useChannelInstances()

  const businessHours = useQuery({
    queryKey: queryKeys.businessHours(tenantId),
    queryFn: () => businessHoursApi.getBusinessHours(),
    enabled,
    staleTime: 60_000,
    refetchIntervalInBackground: false,
  })

  const faqs = useQuery({
    queryKey: queryKeys.faqs(tenantId),
    queryFn: () => getFAQs(),
    enabled,
    staleTime: 60_000,
    refetchIntervalInBackground: false,
  })

  const tasks = useMemo(
    () =>
      buildSetupTasks(profile, {
        hasConnectedChannel: (channels.data ?? []).some(
          (channel) => channel.status === 'CONNECTED' || channel.status === 'WORKING'
        ),
        hasProducts: (activeProducts ?? 0) > 0,
        hasCustomizedBot: Boolean(
          botSettings.data &&
            (botSettings.data.aiEnabled ||
              (botSettings.data.botName !== 'Mi Bot' && botSettings.data.botName !== null))
        ),
        hasEscalationConfigured: Boolean(botSettings.data?.handoffRecipientLabel?.trim()),
        hasFaqs: (faqs.data?.length ?? 0) > 0,
        businessHoursEnabled: businessHours.data?.businessHoursEnabled ?? false,
      }),
    [profile, activeProducts, channels.data, botSettings.data, businessHours.data, faqs.data]
  )

  return {
    tasks,
    isLoading:
      botSettings.isPending || channels.isPending || businessHours.isPending || faqs.isPending,
  }
}
